/** Default sort used by the enrollments list screen (client-side only). */
export const ENROLLMENT_LIST_DEFAULTS = {
  size: 10,
  sortBy: 'participant',
  sortDir: 'asc',
};

/** Sort keys exposed by the enrollments table column headers. */
export const ENROLLMENT_SORT_FIELDS = {
  participant: 'participant',
  courseName: 'courseName',
  inviteAccepted: 'inviteAccepted',
  status: 'status',
};

/**
 * Computes the next client-side sort when a column header is clicked.
 * Same column toggles asc/desc; a new column always starts asc.
 */
export function getNextEnrollmentSort(columnKey, currentSortBy, currentSortDir) {
  const sortBy = columnKey || ENROLLMENT_LIST_DEFAULTS.sortBy;
  if (currentSortBy === sortBy) {
    return {
      sortBy,
      sortDir: currentSortDir === 'asc' ? 'desc' : 'asc',
    };
  }
  return { sortBy, sortDir: 'asc' };
}

/**
 * Stable cache key for a paginated enrollment list query.
 * Keyed by userId + page + size + keyword + courseId so each course filter
 * (including "all") caches its pages separately (sort is client-side only).
 */
export function buildEnrollmentListCacheKey(userId, {
  page = 0,
  size = ENROLLMENT_LIST_DEFAULTS.size,
  keyword = '',
  courseId = '',
} = {}) {
  const kw = (keyword || '').trim();
  const cid = (courseId || '').trim();
  return `enrollments:${userId}:${page}:${size}:${kw}:${cid || 'all'}`;
}

/**
 * Builds query string for paginated enrollment list API calls.
 * Sorting is applied client-side; the API is not called on column-header clicks.
 * courseId is appended only when truthy ("" / undefined => all courses).
 */
export function buildEnrollmentListQuery({
  page = 0,
  size = 10,
  keyword = '',
  courseId = '',
} = {}) {
  const params = new URLSearchParams();
  params.set('page', String(page));
  params.set('size', String(size));
  if (keyword && keyword.trim()) {
    params.set('keyword', keyword.trim());
  }
  if (courseId && String(courseId).trim()) {
    params.set('courseId', String(courseId).trim());
  }
  return params.toString();
}

/** Resolves a row field value for client-side column sorting. */
function enrollmentSortValue(row, sortBy) {
  switch (sortBy) {
    case ENROLLMENT_SORT_FIELDS.courseName:
      return row.courseName ?? '';
    case ENROLLMENT_SORT_FIELDS.inviteAccepted:
      return row.emailVerified ?? 0;
    case ENROLLMENT_SORT_FIELDS.status:
      return row.activestatus ?? 0;
    case ENROLLMENT_SORT_FIELDS.participant:
    default: {
      const name = `${row.firstName || ''} ${row.lastName || ''}`.trim();
      return name || row.studentEmail || '';
    }
  }
}

/**
 * Sorts the current page of enrollment rows in memory — no API round-trip.
 */
export function sortEnrollmentsLocally(rows, sortBy, sortDir) {
  const list = Array.isArray(rows) ? [...rows] : [];
  const dir = sortDir === 'desc' ? -1 : 1;
  const field = sortBy || ENROLLMENT_LIST_DEFAULTS.sortBy;

  list.sort((a, b) => {
    const av = enrollmentSortValue(a, field);
    const bv = enrollmentSortValue(b, field);
    if (typeof av === 'number' && typeof bv === 'number') {
      return (av - bv) * dir;
    }
    return String(av).localeCompare(String(bv), undefined, { sensitivity: 'base' }) * dir;
  });

  return list;
}

/**
 * Parses paginated enrollment list API response; falls back to array payloads for legacy callers.
 */
export function parseEnrollmentListResponse(payload) {
  if (Array.isArray(payload)) {
    return {
      content: payload,
      totalElements: payload.length,
      totalPages: 1,
      page: 0,
      size: payload.length,
    };
  }

  return {
    content: Array.isArray(payload?.content) ? payload.content : [],
    totalElements: Number(payload?.totalElements ?? 0),
    totalPages: Number(payload?.totalPages ?? 0),
    page: Number(payload?.page ?? 0),
    size: Number(payload?.size ?? 10),
  };
}
